<template>
  <vx-card>
    <h4>ผู้มีอำนาจลงนาม</h4>
    <p class="my-4">เลือกผู้ลงนามตามประเภทเอกสาร แล้วบันทึกพร้อมกันทั้งกลุ่ม</p>
    <p v-if="loading" role="status">กำลังโหลดข้อมูล…</p>
    <form v-else-if="rows.length" @submit.prevent="save">
      <div v-for="row in rows" :key="row.id" class="mb-6">
        <label :for="row.id">{{ row.name }}</label>
        <v-select
          :input-id="row.id"
          v-model="draft[row.id]"
          :options="signerPositionOptions"
          :reduce="(option) => option.value"
          :clearable="false"
          :disabled="saving"
        />
        <p v-if="row.description" class="text-sm mt-2">{{ row.description }}</p>
      </div>
      <div class="flex justify-end">
        <vs-button
          type="border"
          class="mr-3"
          :disabled="saving || !dirty"
          @click="reset"
          >คืนค่า</vs-button
        >
        <vs-button :disabled="saving || !dirty" @click="save">{{
          saving ? "กำลังบันทึก…" : "บันทึกผู้ลงนาม"
        }}</vs-button>
      </div>
    </form>
    <p v-if="message" role="status" aria-live="polite" class="mt-4">
      {{ message }}
    </p>
    <vs-button v-if="!loading && !rows.length" type="border" @click="load"
      >ลองใหม่</vs-button
    >
  </vx-card>
</template>

<script>
import vSelect from "vue-select";
import { mapGetters, createNamespacedHelpers } from "vuex";
import { apolloClient } from "@/setup/apollo-setup";
import {
  GET_CONFIG_SIGNER,
  UPDATE_CONFIG_SIGNER,
} from "@/queries/modules/system";
import { ISignerType } from "@/constants/interface";

const { mapActions } = createNamespacedHelpers("Master");

export default {
  components: { vSelect },
  data() {
    return {
      rows: [],
      draft: {},
      loading: true,
      saving: false,
      message: "",
      disposed: false,
    };
  },
  computed: {
    ...mapGetters({ signerPositionOptions: "Master/signerPositionOptions" }),
    dirty() {
      return this.rows.some((row) => this.draft[row.id] !== row.signerId);
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.disposed = true;
  },
  methods: {
    ...mapActions(["getSignerOptions"]),
    reset() {
      this.rows.forEach((row) => this.$set(this.draft, row.id, row.signerId));
      this.message = "";
    },
    async load() {
      this.loading = true;
      this.message = "";
      try {
        await this.getSignerOptions();
        const result = await apolloClient.query({
          query: GET_CONFIG_SIGNER,
          fetchPolicy: "no-cache",
        });
        if (this.disposed) return;
        const records = result.data.configSigner.signerDetails;
        const types = Object.values(ISignerType);
        if (
          types.some(
            (type) => records.filter((row) => row.type === type).length !== 1
          )
        )
          throw new Error("Incomplete signer slots");
        this.rows = types.map((type) =>
          records.find((row) => row.type === type)
        );
        this.reset();
      } catch (error) {
        if (!this.disposed) {
          this.rows = [];
          this.message =
            "โหลดผู้ลงนามไม่สำเร็จ กรุณาตรวจสอบสิทธิ์และข้อมูลประเภทผู้ลงนาม";
        }
      } finally {
        if (!this.disposed) this.loading = false;
      }
    },
    async save() {
      if (this.saving || !this.dirty) return;
      if (this.rows.some((row) => !this.draft[row.id])) {
        this.message = "กรุณาเลือกผู้ลงนามให้ครบ";
        return;
      }
      this.saving = true;
      this.message = "";
      try {
        const result = await apolloClient.mutate({
          mutation: UPDATE_CONFIG_SIGNER,
          variables: {
            payload: {
              signerDetails: this.rows.map((row) => ({
                id: row.id,
                signerId: this.draft[row.id],
              })),
            },
          },
        });
        if (this.disposed) return;
        this.rows = Object.values(ISignerType).map((type) =>
          result.data.updateConfigSigner.signerDetails.find(
            (row) => row.type === type
          )
        );
        this.reset();
        this.message = "บันทึกผู้ลงนามเรียบร้อยแล้ว";
      } catch (error) {
        if (!this.disposed)
          this.message =
            "ไม่สามารถยืนยันผลบันทึกได้ กรุณาโหลดหน้าใหม่เพื่อตรวจสอบค่าก่อนลองอีกครั้ง";
      } finally {
        if (!this.disposed) this.saving = false;
      }
    },
  },
};
</script>
